import { useEffect, useState, type FormEvent } from 'react';
import { Button } from '../ui/Button.js';
import { Input } from '../ui/Input.js';
import { Label } from '../ui/Label.js';
import { ApiError, apiGet, apiPost } from '../../lib/api.js';

type Status = { enabled: boolean; recoveryCodesRemaining: number };
type Enrollment = { otpauthUrl: string; qrDataUrl: string; secret: string };

const GREEN = { backgroundColor: '#E8F3EC', color: '#1E7A47', border: '1px solid #C9E5D2' };

/**
 * Account-settings section for opt-in authenticator-app 2FA: enable (QR → confirm
 * → recovery codes shown once), and when on, regenerate codes / disable (both
 * re-auth with the password). Mirrors the password-change form's inline style.
 */
export function TwoFactorSection() {
  const [status, setStatus] = useState<Status | null>(null);
  const [enrollment, setEnrollment] = useState<Enrollment | null>(null);
  const [recoveryCodes, setRecoveryCodes] = useState<string[] | null>(null);
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [mode, setMode] = useState<'idle' | 'disable' | 'regenerate'>('idle');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function loadStatus() {
    try {
      setStatus(await apiGet<Status>('/auth/2fa/status'));
    } catch {
      /* leave status null; the section just won't render its body */
    }
  }
  useEffect(() => {
    void loadStatus();
  }, []);

  function fail(e: unknown) {
    setError(e instanceof ApiError ? e.message : 'Something went wrong. Try again.');
  }

  async function startEnroll() {
    setError(null);
    setBusy(true);
    try {
      setEnrollment(await apiPost<Enrollment>('/auth/2fa/enroll'));
    } catch (e) {
      fail(e);
    } finally {
      setBusy(false);
    }
  }

  async function confirmEnroll(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const res = await apiPost<{ recoveryCodes: string[] }>('/auth/2fa/confirm', {
        code: code.trim(),
      });
      setRecoveryCodes(res.recoveryCodes);
      setEnrollment(null);
      setCode('');
      await loadStatus();
    } catch (e) {
      fail(e);
    } finally {
      setBusy(false);
    }
  }

  async function submitReauth(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      if (mode === 'disable') {
        await apiPost('/auth/2fa/disable', { password });
      } else {
        const res = await apiPost<{ recoveryCodes: string[] }>(
          '/auth/2fa/recovery-codes/regenerate',
          { password },
        );
        setRecoveryCodes(res.recoveryCodes);
      }
      setMode('idle');
      setPassword('');
      await loadStatus();
    } catch (e) {
      fail(e);
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="surface p-5 sm:p-6 mt-6">
      <h2 className="font-serif text-lg text-ink tracking-tightish mb-1">
        Two-factor authentication
      </h2>
      <p className="text-sm text-muted mb-5">
        Add a code from an authenticator app (Google Authenticator, Authy, 1Password…) to your
        sign-in.
      </p>

      {error && (
        <div className="mb-4 rounded-lg bg-danger-bg border border-danger/20 px-3 py-2 text-sm text-danger">
          {error}
        </div>
      )}

      {/* Recovery codes — shown once after enable / regenerate. */}
      {recoveryCodes ? (
        <div>
          <p className="text-sm text-ink mb-3">
            <strong>Save your recovery codes.</strong> Each can be used once if you lose your device.
            They won&rsquo;t be shown again.
          </p>
          <div className="grid grid-cols-2 gap-2 font-mono text-sm bg-cream-deep/40 border border-line rounded-lg p-4 mb-4">
            {recoveryCodes.map((c) => (
              <span key={c}>{c}</span>
            ))}
          </div>
          <Button variant="secondary" onClick={() => setRecoveryCodes(null)}>
            Done
          </Button>
        </div>
      ) : enrollment ? (
        /* Enrolling — scan QR + confirm a code. */
        <form onSubmit={confirmEnroll} className="space-y-4">
          <p className="text-sm text-muted">
            Scan this with your authenticator app, then enter the 6-digit code to turn 2FA on.
          </p>
          <img
            src={enrollment.qrDataUrl}
            alt="Two-factor QR code"
            className="h-44 w-44 border border-line rounded-lg bg-white"
          />
          <p className="text-xs text-muted">
            Can&rsquo;t scan? Enter this key manually:{' '}
            <span className="font-mono text-ink break-all">{enrollment.secret}</span>
          </p>
          <div>
            <Label htmlFor="totp-code">6-digit code</Label>
            <Input
              id="totp-code"
              inputMode="numeric"
              autoComplete="one-time-code"
              required
              value={code}
              onChange={(e) => setCode(e.target.value)}
              placeholder="123456"
            />
          </div>
          <div className="flex items-center gap-2">
            <Button type="submit" loading={busy}>
              Confirm &amp; enable
            </Button>
            <Button
              type="button"
              variant="ghost"
              disabled={busy}
              onClick={() => {
                setEnrollment(null);
                setCode('');
                setError(null);
              }}
            >
              Cancel
            </Button>
          </div>
        </form>
      ) : mode !== 'idle' ? (
        /* Re-auth for disable / regenerate. */
        <form onSubmit={submitReauth} className="space-y-4 max-w-sm">
          <p className="text-sm text-ink">
            {mode === 'disable'
              ? 'Enter your password to turn off two-factor authentication.'
              : 'Enter your password to generate a fresh set of recovery codes.'}
          </p>
          <div>
            <Label htmlFor="reauth-password">Password</Label>
            <Input
              id="reauth-password"
              type="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••••"
            />
          </div>
          <div className="flex items-center gap-2">
            <Button type="submit" variant={mode === 'disable' ? 'danger' : 'primary'} loading={busy}>
              {mode === 'disable' ? 'Disable 2FA' : 'Regenerate codes'}
            </Button>
            <Button
              type="button"
              variant="ghost"
              disabled={busy}
              onClick={() => {
                setMode('idle');
                setPassword('');
                setError(null);
              }}
            >
              Cancel
            </Button>
          </div>
        </form>
      ) : status?.enabled ? (
        /* Enabled — manage. */
        <div className="space-y-3">
          <span
            className="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium"
            style={GREEN}
          >
            Two-factor is on
          </span>
          <p className="text-sm text-muted">
            {status.recoveryCodesRemaining} recovery code
            {status.recoveryCodesRemaining === 1 ? '' : 's'} remaining.
          </p>
          <div className="flex items-center gap-2">
            <Button variant="secondary" size="sm" onClick={() => setMode('regenerate')}>
              Regenerate recovery codes
            </Button>
            <Button variant="ghost" size="sm" onClick={() => setMode('disable')}>
              Disable 2FA
            </Button>
          </div>
        </div>
      ) : (
        /* Disabled — offer to enable. */
        <Button loading={busy} onClick={startEnroll}>
          Enable two-factor
        </Button>
      )}
    </section>
  );
}
